// src/components/Login.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";

const ALLOW_SELF_SIGNUP = (process.env.REACT_APP_ALLOW_SELF_SIGNUP ?? "true") === "true";

export default function Login() {
  const { user, isAdmin, login, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Redirect once user (and role) are known
  useEffect(() => {
    if (!user) return;
    if (isAdmin) navigate("/admin", { replace: true });
    else navigate("/dashboard", { replace: true });
  }, [user, isAdmin, navigate]);

  if (user) return <div className="p-6 text-[#D4FF00]">Redirecting…</div>;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      // redirect handled by useEffect
    } catch (e) {
      const code = e.code || "";
      if (code.includes("user-not-found") || code.includes("wrong-password"))
        setErr("Invalid email or password.");
      else if (code.includes("too-many-requests"))
        setErr("Too many attempts. Try again later.");
      else if (code.includes("invalid-email"))
        setErr("Please enter a valid email.");
      else setErr("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setErr("");
    if (!email) return setErr("Enter your email to reset password.");
    try {
      await resetPassword(email.trim());
      alert("Password reset email sent.");
    } catch {
      setErr("Could not send reset email. Check the email and try again.");
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl border border-[#D4FF00]/30 bg-black/70 backdrop-blur p-6 shadow-[0_0_40px_rgba(212,255,0,0.15)]">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-extrabold tracking-wide text-[#D4FF00]">
            RevyTech Gaming Login
          </h1>
          <p className="text-sm text-gray-300 mt-1">
            Lets go to our <span className="text-[#D4FF00]">Dashboard</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm mb-1 text-gray-200">Email</label>
            <input
              className="w-full rounded-lg border border-[#D4FF00]/30 bg-neutral-900 text-gray-100 p-2.5 focus:ring-2 focus:ring-[#D4FF00] focus:border-transparent placeholder:text-gray-500"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm mb-1 text-gray-200">Password</label>
            <input
              className="w-full rounded-lg border border-[#D4FF00]/30 bg-neutral-900 text-gray-100 p-2.5 focus:ring-2 focus:ring-[#D4FF00] focus:border-transparent"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {err && <div className="text-[#ff6b6b] text-sm">{err}</div>}

          <button
            disabled={loading}
            className="w-full py-2.5 rounded-lg bg-[#D4FF00] text-black font-semibold hover:brightness-90 transition disabled:opacity-60"
            type="submit"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="mt-3 flex items-center justify-between text-sm">
          <button
            className="underline text-gray-300 hover:text-white"
            onClick={handleReset}
          >
            Forgot password?
          </button>

          {ALLOW_SELF_SIGNUP && (
            <Link className="underline text-gray-300 hover:text-white" to="/signup">
              Create account
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
